if (!customElements.get('redback-gallery-video')) {
  class RedbackGalleryVideo extends HTMLElement {
    constructor() {
      super();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopHover = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.nearViewport = false;
      this.inViewport = false;
      this.hovered = false;
      this.focused = false;
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.card = this.closest('.gallery__item');
      this.template = this.querySelector('template');
      this.reducedMotion.addEventListener('change', this.handlePreferenceChange);
      this.desktopHover.addEventListener('change', this.handleInteractionModeChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange, {
        signal: this.abortController.signal
      });
      this.card?.addEventListener('pointerenter', this.handlePointerEnter, {
        signal: this.abortController.signal
      });
      this.card?.addEventListener('pointerleave', this.handlePointerLeave, {
        signal: this.abortController.signal
      });
      this.card?.addEventListener('focusin', this.handleFocusIn, {
        signal: this.abortController.signal
      });
      this.card?.addEventListener('focusout', this.handleFocusOut, {
        signal: this.abortController.signal
      });

      if ('IntersectionObserver' in window) {
        this.loadObserver = new IntersectionObserver((entries) => {
          this.nearViewport = entries[0].isIntersecting;
          this.updatePlayback();
        }, { rootMargin: '300px 0px' });

        this.playObserver = new IntersectionObserver((entries) => {
          this.inViewport = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.25;
          this.updateActivity();
          this.updatePlayback();
        }, { threshold: [0, 0.25] });

        this.loadObserver.observe(this.card || this);
        this.playObserver.observe(this.card || this);
      } else {
        this.nearViewport = true;
        this.inViewport = true;
        this.updateActivity();
        this.updatePlayback();
      }
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.loadObserver?.disconnect();
      this.playObserver?.disconnect();
      this.reducedMotion.removeEventListener('change', this.handlePreferenceChange);
      this.desktopHover.removeEventListener('change', this.handleInteractionModeChange);
      this.pauseVideo();
    }

    handlePreferenceChange = () => {
      this.updateActivity();
      if (!this.videoAllowed()) {
        this.pauseVideo();
        this.video?.remove();
        this.video = null;
      }
      this.updatePlayback();
    };

    handleInteractionModeChange = () => {
      this.hovered = this.card?.matches(':hover') || false;
      this.updatePlayback();
    };

    handlePointerEnter = () => {
      this.hovered = true;
      this.updatePlayback();
    };

    handlePointerLeave = () => {
      this.hovered = false;
      this.updatePlayback();
    };

    handleFocusIn = () => {
      this.focused = true;
      this.updatePlayback();
    };

    handleFocusOut = (event) => {
      if (this.card?.contains(event.relatedTarget)) return;
      this.focused = false;
      this.updatePlayback();
    };

    handleVisibilityChange = () => {
      this.updateActivity();
      this.updatePlayback();
    };

    videoAllowed() {
      return !this.reducedMotion.matches && !this.saveData;
    }

    updateActivity() {
      this.card?.classList.toggle('is-active', this.inViewport && !document.hidden && this.videoAllowed());
    }

    shouldPlay() {
      return this.videoAllowed() && this.inViewport && !document.hidden &&
        (!this.desktopHover.matches || this.hovered || this.focused);
    }

    updatePlayback() {
      if (!this.videoAllowed() || !this.nearViewport) {
        this.pauseVideo();
        return;
      }
      if (!this.desktopHover.matches || this.hovered || this.focused) this.prepareVideo();
      if (this.shouldPlay()) this.playVideo();
      else this.pauseVideo();
    }

    prepareVideo() {
      if (!this.videoAllowed() || !this.template || this.video || !this.nearViewport) return;
      this.append(this.template.content.cloneNode(true));
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.addEventListener('playing', () => {
        if (this.shouldPlay()) this.card?.classList.add('is-video-playing');
        else this.pauseVideo();
      }, {
        signal: this.abortController.signal
      });
      this.video.load();
    }

    async playVideo() {
      if (!this.video || !this.shouldPlay()) return;
      try {
        await this.video.play();
        if (!this.shouldPlay()) this.pauseVideo();
      } catch (error) {
        this.card?.classList.remove('is-video-playing');
      }
    }

    pauseVideo() {
      if (this.video && !this.video.paused) this.video.pause();
      this.card?.classList.remove('is-video-playing');
    }
  }

  customElements.define('redback-gallery-video', RedbackGalleryVideo);
}
